import type { AttachedDocument } from '../../../lib/types';

interface DocumentPreviewModalProps {
  attached: AttachedDocument;
  open: boolean;
  onClose: () => void;
}

/**
 * DocumentPreviewModal — Item 36 PR-B2.
 *
 * Preview inline para PDF (iframe) + imagem (lightbox); outros tipos via link
 * download apontando para signedUrl TTL 5min. Backdrop click fecha; X fecha.
 */
export function DocumentPreviewModal({
  attached,
  open,
  onClose,
}: DocumentPreviewModalProps): JSX.Element | null {
  if (!open) return null;

  const isPdf = attached.mimeType === 'application/pdf';
  const isImage = attached.mimeType.startsWith('image/');

  return (
    <div
      role="dialog"
      aria-label={`Preview ${attached.name}`}
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
      }}
    >
      {/* Backdrop — button for a11y keyboard close */}
      <button
        type="button"
        aria-label="Clique fora para sair"
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
        }}
      />
      {/* Modal content panel — stop backdrop click from bubbling */}
      <div
        style={{
          position: 'relative',
          background: 'var(--bg-surface)',
          color: 'var(--fg-1)',
          borderRadius: 'var(--radius-lg)',
          maxWidth: '90vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: 12,
            borderBottom: '1px solid var(--border-default)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <strong>{attached.name}</strong>
          <button type="button" onClick={onClose} aria-label="Fechar preview">
            ×
          </button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', minWidth: 480, minHeight: 400 }}>
          {isPdf && (
            <iframe
              title={`Preview ${attached.name}`}
              src={attached.signedUrl}
              style={{ width: '100%', height: '70vh', border: 'none' }}
            />
          )}
          {isImage && (
            <img
              src={attached.signedUrl}
              alt={attached.name}
              style={{ maxWidth: '100%', maxHeight: '70vh', display: 'block' }}
            />
          )}
          {!isPdf && !isImage && (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <p style={{ marginBottom: 16, opacity: 0.7 }}>
                Preview inline não disponível para esse tipo de arquivo.
              </p>
              <a
                href={attached.signedUrl}
                download={attached.name}
                className="btn btn--primary btn--sm"
              >
                Baixar arquivo
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
